import { useState } from "react";

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    textSize: 0,
    grayscale: false,
    highContrast: false,
    negativeContrast: false,
    lightBg: false,
    underlineLinks: false,
    readableFont: false,
  });

  const isActive = settings.textSize !== 0 || settings.grayscale || settings.highContrast || settings.negativeContrast || settings.lightBg || settings.underlineLinks || settings.readableFont;

  const applySettings = (newSettings) => {
    setSettings(newSettings);

    const filters = [];
    if (newSettings.grayscale) filters.push("grayscale(100%)");
    if (newSettings.highContrast) filters.push("contrast(1.5)");
    if (newSettings.negativeContrast) filters.push("invert(1)");

    // Apply filters to body content but not the widget itself
    const existing = document.getElementById("a11y-filter-style");
    if (filters.length) {
      const css = `body > *:not([data-testid="accessibility-widget"]) { filter: ${filters.join(" ")} !important; }`;
      if (existing) { existing.textContent = css; } else {
        const style = document.createElement("style");
        style.id = "a11y-filter-style";
        style.textContent = css;
        document.head.appendChild(style);
      }
    } else if (existing) { existing.remove(); }

    // Light background
    const bgStyle = document.getElementById("a11y-bg-style");
    if (newSettings.lightBg) {
      const css = `body { background-color: #fff !important; } body * { background-color: transparent; } body > *:not([data-testid="accessibility-widget"]) section, body > *:not([data-testid="accessibility-widget"]) div { background-color: #fff !important; color: #000 !important; }`;
      if (bgStyle) { bgStyle.textContent = css; } else {
        const style = document.createElement("style");
        style.id = "a11y-bg-style";
        style.textContent = css;
        document.head.appendChild(style);
      }
    } else if (bgStyle) { bgStyle.remove(); }

    // Text size - apply to html root element so ALL rem-based sizes scale (Tailwind uses rem)
    document.documentElement.style.fontSize = newSettings.textSize !== 0 ? `${100 + newSettings.textSize * 12}%` : "";

    // Readable font
    const fontStyle = document.getElementById("a11y-font-style");
    if (newSettings.readableFont) {
      const css = `body * { font-family: Arial, Helvetica, sans-serif !important; letter-spacing: 0.05em !important; word-spacing: 0.1em !important; }`;
      if (fontStyle) { fontStyle.textContent = css; } else {
        const style = document.createElement("style");
        style.id = "a11y-font-style";
        style.textContent = css;
        document.head.appendChild(style);
      }
    } else if (fontStyle) { fontStyle.remove(); }

    // Underline links
    const linkStyle = document.getElementById("a11y-underline-style");
    if (newSettings.underlineLinks) {
      const css = `a { text-decoration: underline !important; }`;
      if (linkStyle) { linkStyle.textContent = css; } else {
        const style = document.createElement("style");
        style.id = "a11y-underline-style";
        style.textContent = css;
        document.head.appendChild(style);
      }
    } else if (linkStyle) { linkStyle.remove(); }
  };

  const resetAll = () => {
    const defaults = { textSize: 0, grayscale: false, highContrast: false, negativeContrast: false, lightBg: false, underlineLinks: false, readableFont: false };
    applySettings(defaults);
  };

  const toggle = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    applySettings(updated);
  };

  const changeText = (delta) => {
    const newSize = Math.max(-3, Math.min(5, settings.textSize + delta));
    applySettings({ ...settings, textSize: newSize });
  };

  const options = [
    { label: "Increase Text", action: () => changeText(1), icon: "A+", active: settings.textSize > 0 },
    { label: "Decrease Text", action: () => changeText(-1), icon: "A-", active: settings.textSize < 0 },
    { label: "Grayscale", action: () => toggle("grayscale"), icon: "\u25D1", active: settings.grayscale },
    { label: "High Contrast", action: () => toggle("highContrast"), icon: "\u25D0", active: settings.highContrast },
    { label: "Negative Contrast", action: () => toggle("negativeContrast"), icon: "\u25CF", active: settings.negativeContrast },
    { label: "Light Background", action: () => toggle("lightBg"), icon: "\u2600", active: settings.lightBg },
    { label: "Links Underline", action: () => toggle("underlineLinks"), icon: "U", active: settings.underlineLinks },
    { label: "Readable Font", action: () => toggle("readableFont"), icon: "F", active: settings.readableFont },
  ];

  return (
    <div style={{ position: "fixed", left: 0, top: "80px", zIndex: 60 }} data-testid="accessibility-widget">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Accessibility Options"
        data-testid="accessibility-trigger"
        style={{
          width: "42px", height: "42px", borderRadius: "0 8px 8px 0",
          backgroundColor: isActive ? "#D4A017" : "#1a1a2e", color: "#fff",
          border: "none", cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: "20px", boxShadow: "2px 2px 8px rgba(0,0,0,0.3)",
          transition: "background-color 0.3s"
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="4" r="2"/>
          <path d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z"/>
        </svg>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute", left: "42px", top: 0, width: "240px",
            backgroundColor: "#1a1a2e", borderRadius: "0 12px 12px 0",
            boxShadow: "4px 4px 16px rgba(0,0,0,0.4)", padding: "16px",
            color: "#fff"
          }}
          data-testid="accessibility-panel"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "bold", color: "#D4A017", margin: 0 }}>Accessibility Options</h3>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="a11y-close"
              style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "18px", padding: "0 2px", lineHeight: 1 }}
              aria-label="Close accessibility panel"
            >
              &times;
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {options.map((opt, i) => (
              <button
                key={i}
                onClick={opt.action}
                data-testid={`a11y-${opt.label.toLowerCase().replace(/\s/g, '-')}`}
                style={{
                  padding: "8px 4px", borderRadius: "8px", border: "1px solid",
                  borderColor: opt.active ? "#D4A017" : "#333", backgroundColor: opt.active ? "rgba(212,160,23,0.15)" : "rgba(255,255,255,0.05)",
                  color: opt.active ? "#D4A017" : "#ccc", cursor: "pointer",
                  fontSize: "11px", textAlign: "center", transition: "all 0.2s",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: "4px"
                }}
              >
                <span style={{ fontSize: "16px" }}>{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
          {isActive && (
            <button
              onClick={resetAll}
              data-testid="a11y-reset"
              style={{
                marginTop: "10px", width: "100%", padding: "8px",
                borderRadius: "8px", border: "1px solid #D4A017",
                backgroundColor: "transparent", color: "#D4A017",
                cursor: "pointer", fontSize: "12px", fontWeight: "bold"
              }}
            >
              Reset All
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AccessibilityWidget;
